/**
 * Chargement paresseux des pages de l'atlas de ville.
 *
 * Les PNG conservent une URL versionnée, comme les anciennes sources individuelles :
 * le cache ne peut donc jamais associer une feuille de sprites d'une ancienne version
 * au moteur courant. Une page n'est téléchargée que lorsque l'un de ses sprites est
 * réellement nécessaire au rendu.
 */

import { Assets, Spritesheet, type SpritesheetData, type Texture } from 'pixi.js';

import { atlasUrl } from './spriteUrl';

type AtlasManifest = Readonly<Record<string, string>>;

let manifest: Promise<AtlasManifest> | null = null;
const pages = new Map<string, Promise<Spritesheet>>();

function loadManifest(): Promise<AtlasManifest> {
  manifest ??= fetch(atlasUrl('manifest.json')).then(async (response) => {
    if (!response.ok) throw new Error(`Atlas indisponible (${String(response.status)}).`);
    return (await response.json()) as AtlasManifest;
  });
  return manifest;
}

async function loadPage(file: string): Promise<Spritesheet> {
  let page = pages.get(file);
  if (page === undefined) {
    page = Promise.all([
      fetch(atlasUrl(file)).then(async (response) => {
        if (!response.ok)
          throw new Error(`Page d'atlas indisponible (${String(response.status)}).`);
        return (await response.json()) as SpritesheetData;
      }),
      // Les pages sont encodées en WebP : −72 % d'octets sur la bibliothèque entière
      // (14,2 → 4,0 Mo, mesuré), et l'alpha ressort **intact** — vérifié pixel par pixel
      // au build, écart maximal 0/255, donc aucun halo possible autour des sprites.
      Assets.load<Texture>(atlasUrl(file.replace(/\.json$/, '.webp'))),
    ]).then(async ([data, texture]) => {
      const sheet = new Spritesheet(texture, data);
      await sheet.parse();
      return sheet;
    });
    pages.set(file, page);
  }
  return page;
}

/** Retourne une texture de l'atlas, avec les dimensions source conservées par Pixi. */
export async function cityTexture(name: string): Promise<Texture> {
  const file = name.endsWith('.png') ? name : `${name}.png`;
  const page = (await loadManifest())[file];
  if (page === undefined) throw new Error(`Sprite absent de l'atlas : ${file}`);
  const texture = (await loadPage(page)).textures[file];
  if (texture === undefined) throw new Error(`Texture absente de la page atlas : ${file}`);
  return texture;
}
